import React, { useEffect, useId, useRef, useState } from 'react';
import { toast } from 'sonner';

import { t } from 'copy/t';
import { StyledForm, Row } from './styledComponents';
import { useBoothCodeStatus, useSetBoothCode } from '../api/boothCode';

/**
 *
 * BoothCodeForm
 *
 * The account's booth code: one 6-digit code that staff type on any of the
 * account's iPads to open the booth's settings (rails `PUT /api/v3/booth_code`).
 *
 * The code is never shown again once saved: the server keeps only a hash, and
 * this screen only ever learns whether one is set and when. Saving returns a
 * recovery code, which the parent (AccountPage) holds so it survives a tab switch
 * until the owner ticks "saved" and presses Done, and is then gone for good.
 */

const CODE_PATTERN = /^\d{6}$/;

const primaryButton =
  'px-6 py-2 rounded text-sm font-semibold bg-mb-main text-white hover:opacity-90 ' +
  'disabled:bg-[var(--button-disabled)] disabled:text-[var(--button-disabled-foreground)] disabled:cursor-not-allowed ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mb-main ' +
  'motion-safe:transition-colors';
const secondaryButton =
  'px-4 py-2 rounded text-sm font-semibold border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mb-main ' +
  'motion-safe:transition-colors';
const labelClass = 'block text-xs font-semibold tracking-wide mb-1 uppercase';
const inputClass =
  'w-40 border border-gray-300 rounded px-3 py-2 text-lg tracking-[0.4em] font-mono ' +
  'focus:outline-none focus:border-mb-main focus:ring-1 focus:ring-mb-main aria-[invalid=true]:border-red-600';

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

const digitsOnly = (value) => value.replace(/\D/g, '').slice(0, 6);

const errorFor = (serverError) => {
  if (!serverError) return null;
  if (serverError.error_code === 'too_easy') return t('boothCode.errorTooEasy');
  if (serverError.error_code === 'invalid_format') return t('boothCode.errorFormat');
  return null;
};

const RecoveryCodePanel = ({ recoveryCode, onDone }) => {
  const [saved, setSaved] = useState(false);
  const [copyState, setCopyState] = useState('idle');
  const headingRef = useRef(null);
  const checkboxId = useId();

  // Move focus to the panel so a screen reader announces the new code.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCode);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };

  return (
    <section
      aria-labelledby={`${checkboxId}-title`}
      className="mb-6 p-4 rounded border border-mb-main bg-[var(--mb-main-tint)]"
    >
      <h3
        id={`${checkboxId}-title`}
        ref={headingRef}
        tabIndex={-1}
        className="text-base font-semibold text-gray-900 mb-2 focus:outline-none"
      >
        {t('boothCode.recoveryTitle')}
      </h3>
      <p className="text-sm text-gray-700 mb-3">{t('boothCode.recoveryBody')}</p>
      <p id={`${checkboxId}-label`} className={labelClass}>{t('boothCode.recoveryLabel')}</p>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <output
          aria-labelledby={`${checkboxId}-label`}
          data-testid="recovery-code"
          className="font-mono text-xl tracking-wider text-gray-900 bg-white border border-gray-300 rounded px-3 py-2 select-all"
        >
          {recoveryCode}
        </output>
        <button type="button" onClick={copy} className={secondaryButton}>
          {copyState === 'copied' ? t('boothCode.copied') : t('boothCode.copy')}
        </button>
      </div>
      <p role="status" className="text-xs text-gray-600 min-h-[1rem] mb-3">
        {copyState === 'failed' ? t('boothCode.copyFailed') : ''}
      </p>
      <div className="flex items-center gap-2 mb-4">
        <input
          id={checkboxId}
          type="checkbox"
          checked={saved}
          onChange={(e) => setSaved(e.target.checked)}
          className="rounded border-gray-300 text-mb-main focus:ring-mb-main"
        />
        <label htmlFor={checkboxId} className="text-sm text-gray-800">
          {t('boothCode.savedCheckbox')}
        </label>
      </div>
      <button type="button" onClick={onDone} disabled={!saved} className={primaryButton}>
        {t('boothCode.done')}
      </button>
    </section>
  );
};

const BoothCodeForm = ({ recoveryCode, onRecoveryCode, onRecoveryDone }) => {
  const status = useBoothCodeStatus();
  const setCode = useSetBoothCode();
  const [editing, setEditing] = useState(false);
  const [code, setCodeValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showDigits, setShowDigits] = useState(false);
  const [clientError, setClientError] = useState(null);
  const ids = useId();
  const changeButtonRef = useRef(null);

  if (status.isLoading) {
    return (
      <StyledForm as="div" aria-busy="true">
        <p className="text-sm text-gray-600">{t('boothCode.loading')}</p>
      </StyledForm>
    );
  }

  if (status.isError) {
    return (
      <StyledForm as="div">
        <p role="alert" className="text-sm text-red-700 mb-3">{t('boothCode.loadFailed')}</p>
        <button type="button" onClick={() => status.refetch()} className={secondaryButton}>
          {t('boothCode.retry')}
        </button>
      </StyledForm>
    );
  }

  const isSet = !!status.data?.set;
  const showForm = (!isSet || editing) && !recoveryCode;
  const fieldError = clientError || errorFor(setCode.error);
  const errorId = `${ids}-error`;
  const hintId = `${ids}-hint`;

  const resetFields = () => {
    setCodeValue('');
    setConfirm('');
    setShowDigits(false);
    setClientError(null);
    setCode.reset();
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (setCode.isLoading) return;
    if (!CODE_PATTERN.test(code)) {
      setClientError(t('boothCode.errorFormat'));
      return;
    }
    if (code !== confirm) {
      setClientError(t('boothCode.errorMismatch'));
      return;
    }
    setClientError(null);
    setCode.mutate(code, {
      onSuccess: (data) => {
        onRecoveryCode(data.recovery_code);
        setEditing(false);
        setCodeValue('');
        setConfirm('');
        setShowDigits(false);
      },
      onError: (error) => {
        // A refused code is shown next to the field; anything else is a toast.
        if (!errorFor(error)) toast.error(t('toast.boothCode.saveFailed'));
      },
    });
  };

  const onDone = () => {
    onRecoveryDone();
    setCode.reset();
  };

  const onCancel = () => {
    resetFields();
    setEditing(false);
    // Return focus to the control that opened the form.
    requestAnimationFrame(() => changeButtonRef.current?.focus());
  };

  const codeInputProps = {
    type: showDigits ? 'text' : 'password',
    inputMode: 'numeric',
    pattern: '[0-9]*',
    maxLength: 6,
    autoComplete: 'new-password',
    spellCheck: false,
    className: inputClass,
    'aria-invalid': fieldError ? 'true' : 'false',
    'aria-describedby': fieldError ? `${hintId} ${errorId}` : hintId,
  };

  return (
    <StyledForm as="div">
      <Row>
        <p className="text-sm text-gray-700 mb-3">{t('boothCode.intro')}</p>
        <p className="text-sm text-gray-900 font-medium" data-testid="booth-code-status">
          {isSet
            ? t('boothCode.statusSet', { date: formatDate(status.data.set_at) })
            : t('boothCode.statusNotSet')}
        </p>
        <p className="mt-1 text-xs text-gray-500">{t('boothCode.rotateNote')}</p>
      </Row>

      {recoveryCode && <RecoveryCodePanel recoveryCode={recoveryCode} onDone={onDone} />}

      {isSet && !editing && !recoveryCode && (
        <Row>
          <button
            ref={changeButtonRef}
            type="button"
            onClick={() => setEditing(true)}
            className={secondaryButton}
          >
            {t('boothCode.change')}
          </button>
        </Row>
      )}

      {showForm && (
        <form onSubmit={onSubmit} noValidate>
          <div className="flex flex-wrap gap-6">
            <Row>
              <label htmlFor={`${ids}-code`} className={labelClass}>
                {t('boothCode.newCodeLabel')}
              </label>
              <input
                id={`${ids}-code`}
                value={code}
                onChange={(e) => {
                  setCodeValue(digitsOnly(e.target.value));
                  setClientError(null);
                  if (setCode.error) setCode.reset();
                }}
                {...codeInputProps}
              />
            </Row>
            <Row>
              <label htmlFor={`${ids}-confirm`} className={labelClass}>
                {t('boothCode.confirmCodeLabel')}
              </label>
              <input
                id={`${ids}-confirm`}
                value={confirm}
                onChange={(e) => {
                  setConfirm(digitsOnly(e.target.value));
                  setClientError(null);
                  if (setCode.error) setCode.reset();
                }}
                {...codeInputProps}
              />
            </Row>
          </div>
          <p id={hintId} className="-mt-2 mb-2 text-xs text-gray-500">{t('boothCode.codeHint')}</p>
          <p id={errorId} role="alert" className="mb-3 text-xs text-red-600 min-h-[1rem]">
            {fieldError || ''}
          </p>
          <div className="flex items-center gap-2 mb-5">
            <input
              id={`${ids}-show`}
              type="checkbox"
              checked={showDigits}
              onChange={(e) => setShowDigits(e.target.checked)}
              className="rounded border-gray-300 text-mb-main focus:ring-mb-main"
            />
            <label htmlFor={`${ids}-show`} className="text-sm text-gray-700">
              {t('boothCode.showDigits')}
            </label>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={setCode.isLoading || code.length !== 6 || confirm.length !== 6}
              className={primaryButton}
            >
              {setCode.isLoading
                ? t('boothCode.saving')
                : isSet
                  ? t('boothCode.submitChange')
                  : t('boothCode.submitSet')}
            </button>
            {isSet && (
              // Disabled while saving: cancelling then would hide a recovery code
              // the server has already issued.
              <button type="button" onClick={onCancel} disabled={setCode.isLoading} className={secondaryButton}>
                {t('boothCode.cancel')}
              </button>
            )}
          </div>
        </form>
      )}
    </StyledForm>
  );
};

export default BoothCodeForm;
